import "./CardSettings.style.css";

import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { GameCardState } from "../../../../utils/states/ConstructorPageStates/GameCardState";
import {
  DEFAULT_GAME_IMAGE,
  MAX_FILE_SIZE_MB,
} from "../../../../utils/constants/constants";

import crossImage from "./../../../../resources/images/cross.png";

interface CardSettingsProps {
  gameCardState: GameCardState;
  setGameCardState: Dispatch<SetStateAction<GameCardState>>;
}

const MAX_TAG_LENGTH = 21;

export const CardSettings = ({
  gameCardState,
  setGameCardState,
}: CardSettingsProps) => {
  const [tagText, setTagText] = useState<string>("");
  const tagsDivRef = useRef<HTMLDivElement | null>(null);

  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith("image/")) {
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      console.error("Слишком большой файл");
      return;
    }

    const reader = new FileReader();

    reader.onload = (event: ProgressEvent<FileReader>) => {
      const result = event.target?.result as string;
      setGameCardState({
        ...gameCardState,
        image: result,
      });
    };

    reader.onerror = () => {
      console.error("Ошибка чтения файла");
    };

    reader.readAsDataURL(file);
  };

  const updateCaretPosition = useCallback(() => {
    const el = tagsDivRef.current;
    if (el) {
      const range = document.createRange();
      const sel = window.getSelection();

      range.selectNodeContents(el);
      range.collapse(false);

      sel?.removeAllRanges();
      sel?.addRange(range);

      if (tagText === "" && el.textContent !== "") {
        el.textContent = "";
      } else if (tagText !== "" && el.textContent !== tagText) {
        el.textContent = tagText;
        range.selectNodeContents(el);
        range.collapse(false);
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  }, [tagText]);

  useEffect(() => {
    updateCaretPosition();
  }, [gameCardState.tags, tagText, updateCaretPosition]);

  const setCaretToEnd = (element: HTMLElement) => {
    const range = document.createRange();
    const selection = window.getSelection();

    range.selectNodeContents(element);
    range.collapse(false);

    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const handleTagInput = useCallback(
    (event: React.FormEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;
      let currentText = target.textContent || "";

      if (currentText.length > MAX_TAG_LENGTH) {
        currentText = currentText.slice(0, MAX_TAG_LENGTH);
        target.textContent = currentText;
        setCaretToEnd(target);
      }

      setTagText(currentText);
    },
    []
  );

  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setGameCardState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();

        const newTagText = tagText.trim();

        if (newTagText) {
          const maxTags = 3;

          if (gameCardState.tags.length >= maxTags) {
            console.error(`Вы можете добавить не более ${maxTags} тегов.`);
            return;
          }

          setGameCardState((prevState) => {
            const updatedTags = [...prevState.tags, newTagText];
            return { ...prevState, tags: updatedTags };
          });
          setTagText("");
          if (tagsDivRef.current) {
            tagsDivRef.current.textContent = "";
          }
        }
      }
    },
    [tagText, gameCardState.tags.length, setGameCardState]
  );

  const handleRemoveTag = useCallback(
    (indexToRemove: number) => {
      setGameCardState((prevState) => {
        const updatedTags = prevState.tags.filter(
          (_, index) => index !== indexToRemove
        );
        return { ...prevState, tags: updatedTags };
      });
    },
    [setGameCardState]
  );

  return (
    <div className="card_settings_container">
      <div className="card_settings__title header_3">Настройка игры</div>
      <div className="card_settings__name header_4">
        Название
        <input
          autoComplete="off"
          onChange={handleNameChange}
          name="name"
          className="card_settings__name_input text_2"
          value={gameCardState.name}
        />
      </div>
      <div className="card_settings__image header_4">
        Картинка
        <label
          htmlFor="imageUploadInput"
          style={{ cursor: "pointer", display: "block" }}
        >
          <img
            className="card_settings__image_view"
            src={
              gameCardState.image.length
                ? gameCardState.image
                : DEFAULT_GAME_IMAGE
            }
            alt="Изображение для загрузки"
          />
        </label>
        <input
          autoComplete="off"
          id="imageUploadInput"
          className="card_settings__image_input"
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
        />
        <span className="text_3">Рекомендуемое разрешение: 480х360</span>
      </div>
      <div className="card_settings__tags header_4">
        Тэги
        <div className="card_settings__tags_input_wrapper">
          {gameCardState.tags.map((tag, index) => (
            <div className="card_settings__tag_view tag" key={index}>
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(index)}
                className="card_settings__delete_tag_button"
              >
                <img src={crossImage} alt="delete tag" />
              </button>
            </div>
          ))}
          <div
            ref={tagsDivRef}
            className="card_settings__text_input_area"
            onInput={handleTagInput}
            onKeyDown={handleKeyDown}
            contentEditable={true}
            aria-multiline={true}
            aria-valuemax={20}
            data-placeholder={
              gameCardState.tags.length === 0 && tagText === ""
                ? "Добавьте теги..."
                : ""
            }
          />
        </div>
        <span className="text_3">
          Добавьте до трёх тегов в к вашей игре,
          <br />
          чтобы она заинтересовала больше игроков!
        </span>
      </div>
    </div>
  );
};
